import {
  getCoreRowModel,
  getSortedRowModel,
  RowSelectionState,
  SortingState,
  useReactTable,
} from "@tanstack/react-table";
import { useState, useEffect, useCallback } from "react";
import { ListFilterParams } from "components/typings";
import { TaskBase } from "pages/project/common/taskBase.typing";
import { DataTable } from "../../../../components/Datatable/DataTable";
import { useOfferListTable } from "../hooks/useOfferListTable";
import { Offer } from "../offer.typing";

interface OfferListTableProps {
  offers: Offer[];
  groupTaskType?: string;
  shownDetailDataId?: string;
  filterParams: ListFilterParams<TaskBase>;
  canSelect?: boolean;
  onSelectionChange?: (selectedOfferIds: string[]) => void;
}

export function OfferListTable({
  offers,
  groupTaskType,
  shownDetailDataId,
  filterParams,
  canSelect,
  onSelectionChange,
}: OfferListTableProps) {
  const { columns } = useOfferListTable({ filterParams, canSelect });

  const [sorting, setSorting] = useState<SortingState>([]);

  // Initialize with all rows selected if canSelect is true
  const [rowSelection, setRowSelection] = useState<RowSelectionState>(() => {
    if (!canSelect) return {};
    const selection: RowSelectionState = {};
    offers.forEach((offer) => {
      selection[offer.id] = true;
    });
    return selection;
  });

  const table = useReactTable<TaskBase>({
    data: offers,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onRowSelectionChange: (a) => {
      if (groupTaskType !== "TEP") return setRowSelection(a);
      setRowSelection((prevRowSelection) => {
        const selectionState = typeof a === "function" ? a(rowSelection) : a;
        const prevSelectedIds = Object.keys(prevRowSelection).filter((id) => prevRowSelection[id] === true);
        const newSelectedIds = Object.keys(selectionState).filter((id) => selectionState[id] === true);
        return buildTepSelection(newSelectedIds, prevSelectedIds);
      });
    },
    onSortingChange: setSorting,
    getRowId: (offer) => offer.id,
    state: {
      rowSelection,
      sorting,
    },
  });

  useEffect(() => {
    if (onSelectionChange) {
      const selectedOfferIds = Object.keys(rowSelection).filter((key) => rowSelection[key] === true);
      onSelectionChange(selectedOfferIds);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowSelection]);

  const findRelatedOffer = useCallback(
    (offer: Offer): Offer | undefined => {
      if (!offer) return undefined;

      if (offer.taskType === "Translation") {
        return offers.find((o) => o.taskType === "QC 1-REV_ALL" && o.relatedTaskId === parseInt(offer.taskId));
      } else if (offer.taskType === "QC 1-REV_ALL" && offer.relatedTaskId) {
        return offers.find((o) => o.taskType === "Translation" && parseInt(o.taskId) === offer.relatedTaskId);
      }
      return undefined;
    },
    [offers]
  );

  const buildTepSelection = useCallback(
    (newSelectedIds: string[], prevSelectedIds: string[]): RowSelectionState => {
      if (!newSelectedIds || offers.length === 0) return {};

      let finalSelectedIds = [...newSelectedIds];

      // Find which offers were added or removed
      const addedOffers = newSelectedIds.filter((id) => !prevSelectedIds.includes(id));
      const removedOffers = prevSelectedIds.filter((id) => !newSelectedIds.includes(id));

      // For each added offer, find and add its related offer
      addedOffers.forEach((offerId) => {
        const addedOffer = offers.find((o) => o.id === offerId);
        if (addedOffer) {
          const relatedOffer = findRelatedOffer(addedOffer);
          if (relatedOffer && !finalSelectedIds.includes(relatedOffer.id)) {
            finalSelectedIds.push(relatedOffer.id);
          }
        }
      });

      // For each removed offer, find and remove its related offer
      removedOffers.forEach((offerId) => {
        const removedOffer = offers.find((o) => o.id === offerId);
        if (removedOffer) {
          const relatedOffer = findRelatedOffer(removedOffer);
          if (relatedOffer) {
            finalSelectedIds = finalSelectedIds.filter((id) => id !== relatedOffer.id);
          }
        }
      });

      const selection: RowSelectionState = {};
      finalSelectedIds.forEach((id) => {
        selection[id] = true;
      });
      return selection;
    },
    [offers, findRelatedOffer]
  );

  return <DataTable table={table} type="offer" shownDetailDataId={shownDetailDataId} />;
}
